import React from "react";
import { LIVENESS_PHASES } from "./LivenessChallengeMachine";
import { LIVENESS_CONFIG } from "./livenessConfig";

const SEGMENT_COUNT = 64;
const CENTER_X = 50;
const CENTER_Y = 65;
const INNER_RADIUS_X = 36;
const INNER_RADIUS_Y = 47;
const OUTER_RADIUS_X = 40;
const OUTER_RADIUS_Y = 53;
const ACTIVE_COLOR = "#34d399";
const COMPLETE_COLOR = "#22c55e";
const READY_COLOR = "rgba(153, 246, 228, 0.82)";
const IDLE_COLOR = "rgba(255, 255, 255, 0.48)";

const clamp01 = (value) => Math.min(1, Math.max(0, value));

// 摄像头预览经过镜像后，MediaPipe 的正 yaw 对应屏幕右侧，负 yaw
// 对应屏幕左侧。这里按照用户在预览画面里实际看到的方向点亮弧段，
// 不使用人物自身的解剖学左右方向。
const modelDirectionToScreenSide = (direction) =>
  direction === "positive" ? "right" : "left";

const getScreenSide = (yaw) => {
  if (Math.abs(yaw) <= LIVENESS_CONFIG.neutralYawDegrees) return "";
  return yaw > 0 ? "right" : "left";
};

const getVerticalSide = (pitch) => {
  if (Math.abs(pitch) <= LIVENESS_CONFIG.neutralPitchDegrees) return "";
  return pitch > 0 ? "top" : "bottom";
};

// 将左、右、上、下划分为互不重叠的弧段。这样前一个动作完成并保持
// 绿色以后，用户仍然能看清当前动作对应的是哪个方向。
const getSegmentZone = (degrees) => {
  if (degrees >= 60 && degrees < 120) return "bottom";
  if (degrees >= 120 && degrees < 240) return "left";
  if (degrees >= 240 && degrees < 300) return "top";
  return "right";
};

export const getFaceGuideFeedback = (challenge = {}) => {
  const completedSides = new Set();
  if (challenge.completedTurns?.positive) {
    completedSides.add(modelDirectionToScreenSide("positive"));
  }
  if (challenge.completedTurns?.negative) {
    completedSides.add(modelDirectionToScreenSide("negative"));
  }

  const turnPhase = [
    LIVENESS_PHASES.TURN_FIRST_SIDE,
    LIVENESS_PHASES.TURN_RETURN_CENTER,
    LIVENESS_PHASES.TURN_OPPOSITE_SIDE,
  ].includes(challenge.phase);
  const nodPhase = [
    LIVENESS_PHASES.NOD_CENTER,
    LIVENESS_PHASES.NOD_MOVE,
    LIVENESS_PHASES.NOD_RETURN_CENTER,
    LIVENESS_PHASES.NOD_OPPOSITE,
    LIVENESS_PHASES.NOD_FINAL_CENTER,
  ].includes(challenge.phase);

  const completedVerticalSides = new Set();
  if (challenge.completedNods?.positive) completedVerticalSides.add("top");
  if (challenge.completedNods?.negative) completedVerticalSides.add("bottom");

  return {
    complete: Boolean(challenge.complete),
    ready: Boolean(challenge.faceDetected && challenge.positionValid),
    completedSides,
    completedVerticalSides,
    liveSide: turnPhase ? getScreenSide(challenge.yaw || 0) : "",
    liveVerticalSide: nodPhase ? getVerticalSide(challenge.pitch || 0) : "",
    turnIntensity: clamp01(
      Math.abs(challenge.yaw || 0) / LIVENESS_CONFIG.turnThresholdDegrees,
    ),
    nodIntensity: clamp01(
      Math.abs(challenge.pitch || 0) / LIVENESS_CONFIG.nodThresholdDegrees,
    ),
    nodMotionComplete: Boolean(challenge.nodMotionComplete),
    nodDirection: challenge.nodDirection || "",
  };
};

const getSegmentColor = (zone, feedback) => {
  if (feedback.complete) return COMPLETE_COLOR;
  if (feedback.completedSides.has(zone)) return COMPLETE_COLOR;
  if (feedback.completedVerticalSides.has(zone)) return COMPLETE_COLOR;

  if (zone === feedback.liveSide && feedback.turnIntensity > 0) {
    return ACTIVE_COLOR;
  }
  if (zone === feedback.liveVerticalSide && feedback.nodIntensity > 0) {
    return ACTIVE_COLOR;
  }

  if (
    feedback.nodMotionComplete &&
    feedback.completedVerticalSides.size === 0
  ) {
    const completedZone = feedback.nodDirection === "positive" ? "top" : "bottom";
    if (zone === completedZone) return COMPLETE_COLOR;
  }

  return feedback.ready ? READY_COLOR : IDLE_COLOR;
};

export function SegmentedFaceGuide({ challenge }) {
  const feedback = getFaceGuideFeedback(challenge);

  return (
    <svg
      viewBox="0 0 100 130"
      className="h-[78%] w-[68%] overflow-visible drop-shadow-[0_0_8px_rgba(0,0,0,0.35)]"
      aria-hidden="true"
    >
      {Array.from({ length: SEGMENT_COUNT }, (_, index) => {
        const degrees = (index / SEGMENT_COUNT) * 360;
        const radians = (degrees * Math.PI) / 180;
        const zone = getSegmentZone(degrees);
        const x1 = CENTER_X + Math.cos(radians) * INNER_RADIUS_X;
        const y1 = CENTER_Y + Math.sin(radians) * INNER_RADIUS_Y;
        const x2 = CENTER_X + Math.cos(radians) * OUTER_RADIUS_X;
        const y2 = CENTER_Y + Math.sin(radians) * OUTER_RADIUS_Y;
        const isLive =
          zone === feedback.liveSide || zone === feedback.liveVerticalSide;
        const liveIntensity = feedback.liveSide
          ? feedback.turnIntensity
          : feedback.nodIntensity;

        return (
          <line
            key={index}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={getSegmentColor(zone, feedback)}
            strokeWidth={isLive ? 1.25 + liveIntensity * 0.55 : 1.15}
            strokeLinecap="round"
            className="transition-all duration-150"
          />
        );
      })}
    </svg>
  );
}
